import { Menu, MenuItem } from '@nexelem/deprecated-theme';
import { useState, MouseEvent } from 'react';
import { Button, CircularProgress } from '@nexelem/components';
import { useCustomTranslation } from '../../../../../hooks';
import { useGraphneResourcesSoftPossibleTasksResourceId } from '../../../../../queryHooks/productionPlanHooks';
import { useDateFormatter } from '@nexelem/utils';
import { useGraphneResourcesSoftAssignMutation } from '../../../../../queryHooks/productionPlanHooks/useGraphneResourcesSoftAssignMutation';
import { BatchShortViewBase, ResourceDetailsView, TaskShortView } from '@nexelem/graphne-api';
import { DateRange } from './ShiftSelector';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-light-svg-icons';

type AddTaskButtonProps = {
  workerData: ResourceDetailsView;
  dateRange?: DateRange;
};

export const AddTaskButton = ({ workerData, dateRange }: AddTaskButtonProps) => {
  const { t } = useCustomTranslation('products.productionPlan');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);
  const { data, isLoading } = useGraphneResourcesSoftPossibleTasksResourceId(
    { resourceId: workerData.id.toString(), start: dateRange?.from.toISOString(), end: dateRange?.to.toISOString() },
    !!anchorEl,
  );

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div className="w-full">
      <Button
        className="mb-2 w-full"
        StartIcon={<FontAwesomeIcon icon={faPlus} />}
        variant="outlined"
        onClick={handleClick}
      >
        {t('common.add')}
      </Button>
      <Menu anchorEl={anchorEl} className="mt-1" open={open} onClose={handleClose}>
        {isLoading && (
          <MenuItem style={{ width: 336 }}>
            <CircularProgress className="m-auto" />
          </MenuItem>
        )}

        {data?.map((task) =>
          task.tpe === 'Task' ? (
            <TaskMenuItem handleClose={handleClose} key={task.id} task={task} workerData={workerData} />
          ) : (
            <BatchTaskMenuItem handleClose={handleClose} key={task.batchId} task={task} workerData={workerData} />
          ),
        )}
      </Menu>
    </div>
  );
};

type TaskMenuItemProps = {
  task: TaskShortView;
  handleClose: () => void;
  workerData: ResourceDetailsView;
};

const TaskMenuItem = ({ task, handleClose, workerData }: TaskMenuItemProps) => {
  const { tWrapper } = useCustomTranslation('products.productionPlan');
  const timeFormatter = useDateFormatter({ onlyHours: true });
  const assignTask = useGraphneResourcesSoftAssignMutation();

  const { start, end, id, productDetails, queue } = task;

  const handleMenuItemClick = () => {
    handleClose();
    assignTask.mutate({
      task: { id, name: productDetails.name },
      resourceIds: [workerData.id],
      workersData: [workerData],
      validateAvailability: true,
    });
  };

  return (
    <MenuItem className="flex flex-col items-start hover:bg-surface-hover-lighter!" onClick={handleMenuItemClick}>
      <span className="text-s11-sb text-text-secondary">
        {timeFormatter(start)} - {timeFormatter(end)}
      </span>
      <span className="text-s16">{productDetails.name}</span>
      <span className="text-s11-sb text-text-secondary uppercase">
        {tWrapper('workStation')} {queue?.name}
      </span>
    </MenuItem>
  );
};

type BatchTaskMenuItemProps = {
  task: BatchShortViewBase;
  handleClose: () => void;
  workerData: ResourceDetailsView;
};

const BatchTaskMenuItem = ({ task, handleClose, workerData }: BatchTaskMenuItemProps) => {
  const assignTask = useGraphneResourcesSoftAssignMutation(true);
  const timeFormatter = useDateFormatter({ onlyHours: true });
  const { tWrapper } = useCustomTranslation('products.productionPlan');

  const { name, batchId, start, end } = task;

  const handleMenuItemClick = () => {
    handleClose();
    assignTask.mutate({
      task: { id: batchId, name },
      resourceIds: [workerData.id],
      workersData: [workerData],
      validateAvailability: true,
    });
  };

  return (
    <MenuItem className="flex flex-col items-start hover:bg-surface-hover-lighter!" onClick={handleMenuItemClick}>
      <span className="text-s11-sb text-text-secondary">
        {timeFormatter(start)} - {timeFormatter(end)}
      </span>
      <span className="flex items-center gap-1 text-s16">
        {tWrapper('batchId')} {name} ({task.tasks?.length || 0})
      </span>
      <span className="text-s11-sb text-add-periwinkle70 uppercase">{task.queue?.name}</span>
    </MenuItem>
  );
};
